import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { v4 as uuidv4 } from 'uuid';

import {
  emptyProfile,
  parseProfiles,
  serializeProfiles,
} from '#banksync/transfer-profiles';
import type { TransferMatchProfile } from '#banksync/transfer-profiles';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { TransferMatchProfileRow } from './TransferMatchProfileRow';

export function TransferMatchProfilesModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [raw, setRaw] = useSyncedPref('transfer-match-profiles');
  const profiles = parseProfiles(raw);

  function persist(next: TransferMatchProfile[]) {
    setRaw(serializeProfiles(next));
  }

  function upsert(profile: TransferMatchProfile) {
    const idx = profiles.findIndex(p => p.id === profile.id);
    const next =
      idx === -1
        ? [...profiles, profile]
        : profiles.map(p => (p.id === profile.id ? profile : p));
    persist(next);
  }

  function openEditor(profile: TransferMatchProfile) {
    dispatch(
      pushModal({
        modal: {
          name: 'edit-transfer-match-profile',
          options: { profile, onSave: upsert },
        },
      }),
    );
  }

  return (
    <Modal name="transfer-match-profiles">
      {({ state }) => (
        <View style={{ width: 'min(720px, 92vw)', flexShrink: 0 }}>
          <ModalHeader
            title={t('Transfer matching profiles')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          <View
            style={{
              maxHeight: '60vh',
              overflowY: 'auto',
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            {profiles.length === 0 ? (
              <Text
                style={{
                  padding: 12,
                  fontSize: 13,
                  fontStyle: 'italic',
                  color: theme.pageTextSubdued,
                  flexShrink: 0,
                }}
              >
                <Trans>No transfer matching profiles yet</Trans>
              </Text>
            ) : (
              profiles.map(profile => (
                <TransferMatchProfileRow
                  key={profile.id}
                  profile={profile}
                  onEdit={() => openEditor(profile)}
                  onDelete={() =>
                    persist(profiles.filter(p => p.id !== profile.id))
                  }
                  onToggleEnabled={() =>
                    upsert({ ...profile, enabled: !profile.enabled })
                  }
                />
              ))
            )}
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <Button
              variant="primary"
              onPress={() => openEditor(emptyProfile(uuidv4()))}
            >
              <Trans>Add profile</Trans>
            </Button>
            <Button onPress={() => state.close()}>
              <Trans>Close</Trans>
            </Button>
          </View>
        </View>
      )}
    </Modal>
  );
}
